import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Local state for text fields inside React Flow nodes.
 * Prevents cursor-jump caused by zustand store updates triggering re-renders.
 * Syncs to store on blur and after a debounce.
 */
export function useNodeTextField(
  storeValue: string,
  onSync: (value: string) => void,
  debounceMs = 500
) {
  const [localValue, setLocalValue] = useState(storeValue);
  const [focused, setFocused] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  // Sync from store when not focused (e.g. project load)
  useEffect(() => {
    if (!focused) {
      setLocalValue(storeValue);
    }
  }, [storeValue, focused]);

  const onChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const val = e.target.value;
    setLocalValue(val);

    // Debounced sync to store
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => onSync(val), debounceMs);
  }, [onSync, debounceMs]);

  const onFocus = useCallback(() => setFocused(true), []);

  const onBlur = useCallback(() => {
    setFocused(false);
    clearTimeout(timerRef.current);
    onSync(localValue);
  }, [localValue, onSync]);

  return { value: localValue, onChange, onFocus, onBlur };
}
